import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ReactComponent as KitchenIcon } from '../../../icons/KitchenIcon.svg';
import { ReactComponent as FiltersIcon } from '../../../icons/FiltersIcon.svg';

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { ResourceType, ResourceSchema, FilterType, SearchFilterOptionType } from '../../../data/types';
import { useLoadResourcesFilter } from '../../../hooks/apphooks';
import { useCompleteSignIn, useUpdateAccountDetails } from '../../../hooks/authhooks';
import { capitalizeString, getUIDFromAccessToken } from '../../../utils/helpers';
// import StarRating from '../../components/StarRating';
import { FilterModal } from '../../../molecules/Desktop/AppModal';
import { useLoadResourceOptions } from '../../../hooks/apphooks';
import { usStates, USStatesEnum } from '../../../utils/enums';
import PartnerResourceCard from '../../../components/PartnerResourceCard';
import PartnerResourceCardSkeleton from '../../../components/PartnerResourceCardSkeleton';


// const categories: CategoryTab[] = [
//   { id: 'kitchen', label: 'Kitchen Spaces', icon: KitchenIcon },
//   // { id: 'event', label: 'Event Spaces', icon: DocumentationIcon },
//   { id: 'documentation', label: 'Documentations', icon: DocumentationIcon },
//   { id: 'training', label: 'Culinary Training', icon: CredentialIcon },
//   { id: 'funding', label: 'Funding & Financial Resources', icon: FundingIcon },
//   { id: 'expert', label: 'Industry Mentors', icon: ExpertIcon },
//   { id: 'farm', label: 'Local Fresh Food', icon: OrderIcon },
// ];

type PartnerResourcesProps = {
  resourceType: ResourceType
  searchFilterOptions: SearchFilterOptionType
}
const PartnerResources: React.FC<PartnerResourcesProps> = ({ resourceType: propResourceType, searchFilterOptions}) => {
  const { resourceType: paramResourceType } = useParams();
  const [searchParams] = useSearchParams();
  const stateParam = searchParams.get('state');
  const navigate = useNavigate();
  const { user, getSavedResourceTruths } = useCompleteSignIn();
  const {searchTerm, currentFilter, setCurrentFilter: setGlobalFilter, setSignInModalIsOpen} = searchFilterOptions
  const validResourceTypes = useMemo<ResourceType[]>(() => ['kitchen', 'documentation', 'training', 'funding', 'expert', 'farm'], []);
  const resourceTypeFromUrl = paramResourceType || propResourceType;
  const validResourceType = validResourceTypes.includes(resourceTypeFromUrl as ResourceType) ? resourceTypeFromUrl as ResourceType : 'kitchen';
  const profileState = user?.state ? user.state : '';
  const currentUSState = usStates.includes(stateParam as string) 
    ? stateParam as string 
    : (currentFilter.state || profileState || '')
  const [selectedCategory, setSelectedCategory] = useState<ResourceType>(validResourceType);
  const [selectedLocation, setSelectedLocation] = useState<string>(currentUSState); // Empty string means "All"
  const [filterSetting, setFilterSetting] = useState<boolean>(false);
  const [currentResourcePage, setCurrentResourcePage] = useState(1);
  // const [showScrollArrow, setShowScrollArrow] = useState<boolean>(false);
  const [showLeftArrow, setShowLeftArrow] = useState<boolean>(false);
  const [showRightArrow, setShowRightArrow] = useState<boolean>(false);
  const categoriesContainerRef = React.useRef<HTMLDivElement>(null);

  const { allOptions } = useLoadResourceOptions()


  // // Get resources
  // const { filteredResources, getPage, reloadResources } = useLoadResourcesFilter(
  //   selectedCategory,
  //   selectedLocation,
  //   true, // active only
  //   'zipcode'
  // );

  // Get resources with proper dependencies in the queryKey
  const { filteredResources, getPage, searchResources, reloadResources, isLoadingResources } = useLoadResourcesFilter(
    selectedCategory, 
    selectedLocation, 
    currentFilter.active,
    currentFilter.sortBy
  )

  // Sync selectedCategory with URL params (URL params are the source of truth)
  // This effect runs when URL params change and updates selectedCategory accordingly
  useEffect(() => {
    const resourceTypeFromUrl = paramResourceType || propResourceType;
    const newResourceType = validResourceTypes.includes(resourceTypeFromUrl as ResourceType) 
      ? resourceTypeFromUrl as ResourceType 
      : 'kitchen';
    
    // Use functional update to avoid dependency on selectedCategory
    setSelectedCategory(prev => {
      if (prev !== newResourceType) {
        return newResourceType;
      }
      return prev;
    });
  }, [paramResourceType, propResourceType, validResourceTypes]);

  // Sync when currentFilter.resourceType changes (from FilterOptions modal)
  // Only sync from filter if URL param is missing or invalid (URL params take precedence)
  useEffect(() => {
    // const resourceTypeFromUrl = paramResourceType || propResourceType;
    const hasValidUrlParam = paramResourceType && validResourceTypes.includes(paramResourceType as ResourceType);
    
    // Only sync from filter if:
    // 1. No valid URL param exists (URL params always take precedence)
    // 2. Filter has a resourceType that's valid
    // 3. Filter resourceType is different from what's currently selected
    if (!hasValidUrlParam && currentFilter.resourceType) {
      const isValidType = validResourceTypes.includes(currentFilter.resourceType);
      if (isValidType) {
        // Use functional update to avoid dependency on selectedCategory
        setSelectedCategory(prev => {
          if (prev !== currentFilter.resourceType) {
            return currentFilter.resourceType;
          }
          return prev;
        });
      }
    }
  }, [currentFilter.resourceType, paramResourceType, validResourceTypes]);

  // Sync state from URL params to filter (URL params are source of truth)
  useEffect(() => {
    if (stateParam !== currentFilter.state) {
      const newState = usStates.includes(stateParam as string) ? stateParam as string : '';
      if (newState !== currentFilter.state) {
        setGlobalFilter(prev => ({ ...prev, state: newState }));
      }
    }
  }, [stateParam, currentFilter.state, setGlobalFilter]);

  // Reset to page 1 when filters change
  useEffect(() => {
    document.title = `HomeCook | ${capitalizeString(selectedCategory)} Resources`;
    setSelectedLocation(currentUSState);
    setCurrentResourcePage(1);
    reloadResources();
  }, [stateParam, currentUSState, selectedCategory, currentFilter.active, currentFilter.sortBy, reloadResources]);


  // Apply search filtering if needed
  const resourcesFiltered = useMemo(() => {
    if (searchTerm) {
      console.log(`Applying search filter: "${searchTerm}"`)
      const { searchedResources } = searchResources(searchTerm)
      return searchedResources
    }
    return filteredResources || []
  }, [filteredResources, searchTerm, searchResources])

  useEffect(() => {
    if (resourcesFiltered?.length > 0) {
      setCurrentResourcePage(1);
    }
  }, [resourcesFiltered?.length]);

  // Get paginated resources
  const {
    paginatedResources,
    numAllResources,
    startIndex,
    endIndex,
  } = useMemo(() => {
    return getPage(resourcesFiltered || [], currentResourcePage);
  }, [resourcesFiltered, currentResourcePage, getPage]);

  // Get user info for save functionality
  const loggedinUid = getUIDFromAccessToken(localStorage.getItem('accessToken'));
  const savedResources = getSavedResourceTruths();
  const { saveResource, removeResource } = useUpdateAccountDetails(loggedinUid);

  // Create filter state for FilterModal
  // const currentFilter: FilterType = {
  //   state: selectedLocation,
  //   active: true,
  //   sortBy: 'zipcode',
  // };

  const setCurrentFilter = (filter: React.SetStateAction<FilterType>) => {
    const newFilter = typeof filter === 'function' ? filter(currentFilter) : filter;
    setSelectedLocation(newFilter.state);
    setGlobalFilter(newFilter);
    
    // Update URL when state changes (from map panning or filter changes)
    if (newFilter.state !== stateParam) {
      navigate(`/${selectedCategory}${newFilter.state ? `?state=${newFilter.state}` : ''}`, { replace: true });
    }
  };

  const handleCardClick = (resource: ResourceSchema) => {
    if (resource.id && selectedCategory) {
      navigate(`/${selectedCategory}/${resource.id}`);
    }
  };

  const handleSaveResource = (resource: ResourceSchema) => {
    if (resource && resource.id) {
      saveResource(resource.id);
    }
  };

  const handleRemoveSavedResource = (resource: ResourceSchema) => {
    if (resource && resource.id) {
      removeResource(resource.id);
    }
  };

  // Get category icon
  const CategoryIcon = allOptions.find(c => c.path === selectedCategory)?.icon || KitchenIcon;

  // Get all US states for location tabs (including "All" option)
  // "All" is always shown first
  const availableStates = useMemo(() => {
    const allStateAbbreviations = Object.keys(USStatesEnum);
    
    // Sort all states alphabetically by full name
    const sortedStates = allStateAbbreviations.sort((a, b) => {
      return USStatesEnum[a].fullName.localeCompare(USStatesEnum[b].fullName);
    });
    
    // Build the final list: "All" first, then selected location (if not "All"), then rest
    const result: string[] = [];
    
    // Add "All" option first
    result.push('');
    
    // If a specific location is selected (not "All"), add it second
    if (selectedLocation && selectedLocation !== '') {
      result.push(selectedLocation);
      // Remove selected location from sorted list
      const selectedIndex = sortedStates.indexOf(selectedLocation);
      if (selectedIndex > -1) {
        sortedStates.splice(selectedIndex, 1);
      }
    }
    
    // Add remaining states
    result.push(...sortedStates);
    
    return result;
  }, [selectedLocation]);

  const handleCategoryChange = (categoryId: string) => {
    if (categoryId === 'event') {
      // Handle event spaces if needed
      return;
    }
    const newResourceType = categoryId as ResourceType;
    // Update filter first (like FilterOptions does) - use functional update to avoid stale closure
    setGlobalFilter(prev => ({ ...prev, resourceType: newResourceType }));
    // Navigate after updating filter (like FilterOptions does)
    navigate(`/${categoryId}${currentFilter.state ? `?state=${currentFilter.state}` : ''}`);
  };

  // Check for overflow and show/hide scroll arrows
  useEffect(() => {
    // Capture the ref value at the start of the effect
    const container = categoriesContainerRef.current;
    
    const checkOverflow = () => {
      if (container) {
        const hasOverflow = container.scrollWidth > container.clientWidth;
        const canScrollLeft = container.scrollLeft > 0;
        const canScrollRight = 
          container.scrollLeft < container.scrollWidth - container.clientWidth - 1;
        
        // setShowScrollArrow(hasOverflow);
        setShowLeftArrow(hasOverflow && canScrollLeft);
        setShowRightArrow(hasOverflow && canScrollRight);
      }
    };

    // Use setTimeout to ensure DOM is fully rendered
    const timeoutId = setTimeout(checkOverflow, 0);
    
    // Add scroll event listener using the captured container reference
    if (container) {
      container.addEventListener('scroll', checkOverflow);
    }
    
    window.addEventListener('resize', checkOverflow);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkOverflow);
      // Use the captured container reference in cleanup
      if (container) {
        container.removeEventListener('scroll', checkOverflow);
      }
    };
  }, [allOptions]);

  // Handle scroll on arrow click
  const handleScrollRight = () => {
    if (categoriesContainerRef.current) {
      const container = categoriesContainerRef.current;
      const scrollAmount = 200; // Scroll by 200px
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const handleScrollLeft = () => {
    if (categoriesContainerRef.current) {
      const container = categoriesContainerRef.current;
      const scrollAmount = 200; // Scroll by 200px
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8 top-10 relative">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-raleway font-bold text-3xl sm:text-4xl">
            <span className="text-[#1A1A1A]">Resources Curated For Your </span>
            <span className="text-primaryDark-400">Food Business Growth</span>
          </h1>
        </div>

        {/* Category Tabs */}
        <div className="flex bg-[#FAFAFA] px-3 py-4 rounded-lg items-center gap-3 mb-6 relative">
          {/* Left Scroll Arrow - Only show if there's overflow and can scroll left */}
          {showLeftArrow && (
            <button
              onClick={handleScrollLeft}
              className="flex items-center justify-center w-8 h-8 rounded-full bg-white border border-[#E9E9E9] hover:bg-gray-50 flex-shrink-0"
              aria-label="Scroll left"
            >
              <ArrowBackIcon className="w-4 h-4 text-[#1A1A1A]" />
            </button>
          )}

          <div
            ref={categoriesContainerRef}
            className="flex items-center gap-3 overflow-x-auto flex-1 [&::-webkit-scrollbar]:hidden"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {allOptions.map((category) => {
              const isActive = selectedCategory === category.path;
              const Icon = category.icon;
              return (
                <button
                  key={category.path}
                  onClick={() => handleCategoryChange(category.path)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-raleway border-0 text-[16px] font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
                    isActive
                      ? 'bg-primaryDark-200 text-white'
                      : 'bg-white text-[#1A1A1A]'
                  }`}
                >
                  <Icon style={{color: isActive ? '#FFFFFF' : '#A1A1A1'}} className={`w-5 h-5`} />
                  <span>{category.name}</span>
                </button>
              );
            })}
          </div>

          {/* Right Scroll Arrow - Only show if there's overflow and can scroll right */}
          {showRightArrow && (
            <button
              onClick={handleScrollRight}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-[#FAFAFA] border-0 p-2 hover:bg-gray-50 flex-shrink-0"
              aria-label="Scroll right"
            >
              <ArrowForwardIcon className="w-4 h-4 text-[#1A1A1A]" />
            </button>
          )}

          {/* Filters Button */}
          <button
            onClick={() => setFilterSetting(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-[#1A1A1A] border border-[#E9E9E9] font-open-sans text-sm font-medium hover:bg-gray-50 flex-shrink-0"
          >
            <FiltersIcon className="w-5 h-5" />
            <span>Filters</span>
          </button>
        </div>

        {/* Location Tabs */}
        <div className="relative mb-8">
          <div className="flex items-center gap-6 overflow-x-auto pb-0 border-b border-[#E9E9E9]">
            {availableStates.map((stateAbbr) => {
              const isActive = selectedLocation === stateAbbr;
              // Handle "All" option (empty string)
              const displayName = stateAbbr === '' ? 'All' : (USStatesEnum[stateAbbr]?.fullName || stateAbbr);
              
              return (
                <button
                  key={stateAbbr || 'all'}
                  onClick={() => {
                    setSelectedLocation(stateAbbr)
                    setCurrentFilter({...currentFilter, state: stateAbbr})
                    navigate(`/${selectedCategory}${stateAbbr ? `?state=${stateAbbr}` : ''}`);
                    // navigate(`/${selectedCategory}?state=${stateAbbr}&active=${currentFilter.active}&sortBy=${currentFilter.sortBy}`);
                  }}
                  className={`font-raleway text-[16px] font-semibold whitespace-nowrap border-0 transition-colors relative pb-2 ${
                    isActive
                      ? 'text-primaryDark-400'
                      : 'text-[#1A1A1A] hover:text-primaryDark-400'
                  }`}
                >
                  {displayName}
                  {isActive && (
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-primaryDark-400 -mb-[1px]" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Resource Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {isLoadingResources ? (
            // Show skeleton loaders while resources are being fetched
            Array.from({ length: 9 }).map((_, index) => (
              <PartnerResourceCardSkeleton key={`skeleton-${index}`} />
            ))
          ) : paginatedResources && paginatedResources.length > 0 ? (
            paginatedResources.map((resource: ResourceSchema) => {
              const isSaved = !!(savedResources && resource?.id && savedResources[resource.id]);

              return (
                <PartnerResourceCard
                  key={resource.id}
                  resource={resource}
                  selectedCategory={selectedCategory}
                  categoryIcon={CategoryIcon}
                  isSaved={isSaved}
                  onCardClick={handleCardClick}
                  onSaveResource={() => {
                    if (resource && resource.id) {
                      if (!loggedinUid && setSignInModalIsOpen) {
                        setSignInModalIsOpen(true);
                      } else if (loggedinUid) {
                        handleSaveResource(resource);
                      }
                    }
                  }}
                  onRemoveSavedResource={handleRemoveSavedResource}
                />
              );
            })
          ) : (
            <div className="col-span-full text-center py-12 text-[#013847]/60">
              No {selectedCategory} resources to show
            </div>
          )}
        </div>

        {/* Pagination */}
        {numAllResources > 0 && (
          <div className="flex justify-between items-center mt-8">
            <span className="text-sm text-[#8A8A8A]">
              Showing {startIndex + 1} - {endIndex} of {numAllResources}
            </span>
            <div className="flex gap-4">
              <button
                onClick={() => setCurrentResourcePage((prev) => Math.max(1, prev - 1))}
                disabled={currentResourcePage === 1}
                className="flex items-center border-0 gap-1 text-sm text-[#1A1A1A] disabled:opacity-50 disabled:cursor-not-allowed hover:text-primaryDark-400"
              >
                <ArrowBackIosIcon className="w-4 h-4" />
                Previous
              </button>
              <button
                onClick={() =>
                  setCurrentResourcePage((prev) =>
                    endIndex < numAllResources ? prev + 1 : prev
                  )
                }
                disabled={endIndex >= numAllResources}
                className="flex items-center border-0 gap-1 text-sm text-[#1A1A1A] disabled:opacity-50 disabled:cursor-not-allowed hover:text-primaryDark-400"
              >
                Next
                <ArrowForwardIosIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Filter Modal */}
      <FilterModal
        resources={filteredResources || []}
        isOpen={filterSetting}
        setIsOpen={setFilterSetting}
        currentFilter={currentFilter}
        setCurrentFilter={setCurrentFilter}
        closeAppModal={() => setFilterSetting(false)}
      />
    </div>
  );
};

export default PartnerResources;
